import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import {
  COMPOSER_IMAGE_ACCEPT,
  MAX_COMPOSER_IMAGES,
  type ComposerUploadStage,
} from '../services/composerImageUpload';
import type { ComposerImageItem } from '../hooks/useComposerImages';

interface Props {
  items: ComposerImageItem[];
  remaining: number;
  disabled?: boolean;
  error?: string | null;
  onAdd: (files: File[]) => Promise<void>;
  onRemove: (id: string) => void;
}

export function QuickTextImageStrip({
  items,
  remaining,
  disabled,
  error,
  onAdd,
  onRemove,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const canAdd = !disabled && remaining > 0;

  const onPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).slice(0, remaining);
    event.target.value = '';
    if (files.length > 0) await onAdd(files);
  };

  if (items.length === 0 && !error) return null;

  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {items.map((item) => {
            const previewUrl = item.remoteUrl ?? item.previewUrl;
            const tone = toneForStatus(item.status);

            return (
              <div
                key={item.id}
                className={cn(
                  'relative aspect-square overflow-hidden rounded-2xl border bg-bg-elevated',
                  tone.border
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpen(previewUrl)}
                  className="absolute inset-0"
                >
                  <img
                    src={previewUrl}
                    alt={item.file.name}
                    className="h-full w-full object-cover"
                  />
                </button>

                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className="absolute right-2 top-2 z-10 rounded-full bg-black/55 px-2 py-1 text-[10px] text-white"
                >
                  删除
                </button>

                {item.status !== 'uploaded' && (
                  <div
                    className={cn(
                      'absolute inset-x-0 bottom-0 z-10 p-2 text-[10px] text-white backdrop-blur-sm',
                      tone.overlay
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span>{labelForStatus(item.status)}</span>
                      <span>{Math.round(item.progress * 100)}%</span>
                    </div>
                    <div className="mt-1 h-1 overflow-hidden rounded-full bg-white/25">
                      <div
                        className={cn('h-full rounded-full transition-all', tone.progress)}
                        style={{ width: `${Math.max(6, Math.round(item.progress * 100))}%` }}
                      />
                    </div>
                    {item.error && <div className="mt-1 line-clamp-2 text-[10px]">{item.error}</div>}
                  </div>
                )}
              </div>
            );
          })}

          {canAdd && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-square rounded-2xl border-2 border-dashed border-border bg-bg-elevated/60 p-3 text-left transition-colors hover:border-accent/50 hover:bg-accent/5"
            >
              <div className="flex h-full flex-col justify-between">
                <div className="text-2xl leading-none text-fg-muted">+</div>
                <div className="space-y-1">
                  <div className="text-xs font-medium text-fg">添加图片</div>
                  <div className="text-[11px] text-fg-subtle">
                    剩余 {remaining}/{MAX_COMPOSER_IMAGES}
                  </div>
                </div>
              </div>
            </button>
          )}
        </div>
      )}

      {error && <div className="text-xs text-danger">{error}</div>}

      <input
        ref={inputRef}
        type="file"
        accept={COMPOSER_IMAGE_ACCEPT}
        multiple
        hidden
        onChange={(event) => void onPick(event)}
      />

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
            className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-6"
          >
            <motion.img
              src={open}
              alt=""
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="max-h-full max-w-full rounded-lg shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function labelForStatus(status: ComposerUploadStage): string {
  if (status === 'requesting_upload_url') return '申请上传';
  if (status === 'uploading') return '上传图片';
  if (status === 'confirming') return '确认文件';
  if (status === 'failed') return '上传失败';
  return '等待中';
}

function toneForStatus(status: ComposerUploadStage): {
  border: string;
  overlay: string;
  progress: string;
} {
  if (status === 'failed') {
    return {
      border: 'border-danger/40',
      overlay: 'bg-danger/75',
      progress: 'bg-white',
    };
  }
  return {
    border: status === 'uploaded' ? 'border-border' : 'border-accent/30',
    overlay: 'bg-black/45',
    progress: 'bg-white',
  };
}
